<template>
  <div
    class="section-container"
    :class="{ 'extra-row': currentGrade.grade == 3 }"
  >
    <p class="title">{{ currentSection.name }}</p>
    <p class="subtitle">{{ currentSection.title }}</p>
    <div class="grades-row">
      <GradeButton
        v-for="grade in currentSection.grades"
        :key="grade"
        :grade="grade"
        :activated="hasSelectedGrade"
        :selected="gradeBoundaries.boundary == grade"
      />
    </div>
    <br />
    <textarea
      class="textarea"
      v-model="currentGrade.comments"
      placeholder="Please provide any other comments on the quality of this theme"
    ></textarea>
    <div class="evaluation">
      <p class="subtitle--bold">Evaluation Criteria</p>
      <div class="section-tip">
        <span>
          <i class="far fa-question-circle"></i>
          <p @click="showFlowDiagram = true">
            How do I choose the correct criteria?
          </p>
        </span>
        <span v-if="hasTooltip">
          <i class="far fa-question-circle"></i>
          <p @mouseenter="showToolTip = true" @mouseleave="showToolTip = false">
            What does '{{ tooltip.for }}' look like?
          </p>
        </span>
      </div>
      <div class="accordion-container" id="accordion">
        <CriteriaAccordion
          v-for="criteria in criterion"
          :key="criteria"
          :criterion="currentSection.criteria[criteria]"
          :negatable="currentSection.negatable[criteria] || []"
          :criteria="criteria"
          :hasMaster="criteria === 'requires_improvement'"
          :savedData="savedSection"
          :info="getInfoForCriteria(criteria)"
          @is-toggled="accordionToggled"
          @accordion-item-toggled="accordionItemToggled"
        />
      </div>
      <span class="tooltip" :class="{ visible: showToolTip }">
        {{ tooltip.text }}
      </span>
    </div>
    <div class="button-row">
      <div class="info-row">
        <p class="link link--back"></p>
      </div>
      <div class="info-row info-row--right">
        <p class="link" :class="{ disabled: !hasSelectedGrade }">
          <a @click="next">Next Theme</a>
          <i class="fas fa-chevron-circle-right"></i>
        </p>
      </div>
    </div>
    <div
      class="flow-modal"
      v-if="showFlowDiagram"
      @click="showFlowDiagram = false"
    >
      <div class="diagram">
        <img class="img" src="@/assets/grading-flow.png" alt="" />
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-prototype-builtins */
import { sections } from '@/mocks/audit'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import GradeButton from '@/components/Sections/Grade/GradeButton.vue'
import CriteriaAccordion from '@/components/Sections/Criteria/Accordion.vue'
import { displayWarning } from '@/utils/notification-factory'
import { message } from '@/utils/app-messages'

const halfPointGoodCriteriaArray = {
  good: [
    'Grammar statements including no acronyms have been used or have been clearly explained where they have been included',
    'The theme is clear, concise, understandable and accessible to parents/carers, CYP and providers/practitioners'
  ]
}

const requiresImprovementArray = {
  good: [
    "Child / young person's details are provided and are correct (e.g.name, DOB, pronoun etc)",
    'Advice provided within 6 weeks of the date of request from the local authority'
  ]
}

export default {
  name: 'SectionTemplate',
  props: ['id'],
  components: { GradeButton, CriteriaAccordion },
  data() {
    return {
      showToolTip: false,
      showFlowDiagram: false,
      openAccordion: null,
      notApplicableRegex: /^Not applicable - /,
      currentGrade: {
        grade: null,
        comments: null,
        criteria: {},
        notApplicable: {
          outstanding: 0,
          good: 0,
          requires_improvement: 0,
          inadequate: 0
        }
      },
      negatable: {
        outstanding: [],
        good: [],
        requires_improvement: [],
        inadequate: []
      }
    }
  },
  computed: {
    ...mapGetters({
      scoreKeyLookup: 'audit/scoreKeyLookup',
      scoreLookup: 'audit/scoreLookup',
      currentAudit: 'audit/currentAudit'
    }),
    mustGradeSenHealth() {
      return (
        this.currentAudit.sections.filter(
          s => s.determination === 'not_applicable'
        ).length > 0
      )
    },
    currentSectionId() {
      return this.id.toUpperCase()
    },
    sectionOrder() {
      return Reflect.ownKeys(sections)
    },
    currentSectionNumber() {
      return this.sectionOrder.findIndex(id => id === this.id)
    },
    currentSection() {
      return sections[this.id]
    },
    hasTooltip() {
      return Object.keys(this.currentSection.tooltip).length > 0
    },
    criterion() {
      return Object.keys(this.currentSection.criteria)
    },
    tooltip() {
      const tooltip = {
        for: null,
        text: null
      }

      if (!this.hasTooltip) {
        return tooltip
      }

      tooltip.for = Object.keys(this.currentSection.tooltip)[0]
      tooltip.text = this.currentSection.tooltip[tooltip.for][0]

      return tooltip
    },
    isLastPage() {
      return this.currentSectionNumber + 1 >= this.sectionOrder.length
    },
    nextPage() {
      return this.isLastPage
        ? 'audit/next-steps'
        : `section/${this.sectionOrder[this.currentSectionNumber + 1]}`
    },
    gradedCriteria() {
      return this.currentGrade.criteria
    },
    weighting() {
      if (
        !Object.prototype.hasOwnProperty.call(this.currentSection, 'weighting')
      ) {
        return 0
      }
      return Number.parseFloat(this.currentSection.weighting.toPrecision(2))
    },
    computedSum() {
      const keys = Object.keys(this.currentGrade.criteria)

      const sum = keys.reduce(
        (accum, key) =>
          accum + this.gradedCriteria[key].length * this.scoreKeyLookup[key],
        0
      )
      return Number(sum)
    },
    computedGrade() {
      return Number(this.computedSum * this.weighting)
    },
    gradeBoundaries() {
      // Return early if currentGrade criteria is 0
      if (Object.keys(this.currentGrade.criteria).length === 0) return 0

      const boundaries = {
        boundary: this.getInitialBoundaryFromComptedGrade(),
        determination: null,
        hasOutstanding: false,
        numOutstanding: 0
      }

      // Get all selected gradings
      const gradedNotApplicable = this.getGradedCriteriaLength('not_applicable')
      const gradedInadequate = this.getGradedCriteriaLength('inadequate')
      const gradedRequiresImprovement = this.getGradedCriteriaLength('requires_improvement')

      // Get number of grading available in current section
      // EXCEPTION: To hack in HalfPoints scores in for some good criteria I reduce the available points and current graded points.
      const gradedGoodHalfPoints = this.getGoodExceptionHalfPoints('good')

      const gradedGood = this.getGradedCriteriaLength('good') - gradedGoodHalfPoints
      const currentGradedGoodHalfPoints = this.getGoodCurrentHalfPointLength('good')

      const availableGoodCriteria = (this.getCurrentSectionLength('good') - currentGradedGoodHalfPoints)

      const gradedOutstanding = this.getGradedCriteriaLength('outstanding')
      const availableOutstanding = this.getCurrentSectionLength('outstanding')

      // **
      //
      // Now we have all the current grading we can determine the section boundaries
      //
      // **

      // If gradedNotApplicable then return early with a not_applicable grading
      if (gradedNotApplicable) {
        boundaries.boundary = 'na'
        boundaries.determination = 'not_applicable'

        return boundaries
      }

      // if 1 gradedInadequate then return early with requires_improvement
      if (gradedInadequate === 1) {
        boundaries.boundary = 3
        boundaries.determination = 'requires_improvement'

        return boundaries
      }

      // if 2 or more gradedInadequate then return early with inadequate grading
      if (gradedInadequate >= 2) {
        boundaries.boundary = 4
        boundaries.determination = 'inadequate'

        return boundaries
      }

      // EXCEPTION: If the 2 criteria in requiresImprovementArray exists and are not selected in the good criteria then it should be requires_improvement
      const hasRequireImprovementCriteriaGraded = this.hasRequireImprovementCriteriaGraded('good', requiresImprovementArray)

      // if gradedRequiresImprovement then return early with requires_improvement grading
      if ((gradedRequiresImprovement > 0) || !hasRequireImprovementCriteriaGraded) {
        boundaries.boundary = 3
        boundaries.determination = 'requires_improvement'

        return boundaries
      }

      // if the majority of good are selected
      if (gradedGood) {
        const nonApplicableGoodCriteria = this.getNegatableCount('good')

        // calculate the majority required to be good
        const majority = Math.round(((availableGoodCriteria - nonApplicableGoodCriteria) / 2) * 100) / 100

        if (gradedGood > majority) {
          boundaries.boundary = 2
          boundaries.determination = 'good'
        } else {
          boundaries.boundary = 3
          boundaries.determination = 'requires_improvement'
        }
      }

      // if the majority of gradedOutstanding are selected
      if (gradedOutstanding) {
        const gradedOutstandingNegatable = this.getNegatableCount('outstanding')
        const majority = Math.round(((availableOutstanding - gradedOutstandingNegatable) / 2) * 100) / 100

        if (gradedOutstanding > majority) {
          boundaries.boundary = 2
          boundaries.determination = 'good'
        } else if (boundaries.determination !== 'good') {
          boundaries.boundary = 3
          boundaries.determination = 'requires_improvement'
        }

        // EXCEPTION: If the first outstanding "The theme meets the criteria for a good judgement consistently and securely"
        // is selected then the grading is good
        const theFirstOutstandingCriteria = this.currentSection.criteria.outstanding[0]

        // It may not be the first item in the graded array
        if (this.gradedCriteria.outstanding.includes(theFirstOutstandingCriteria)) {
          boundaries.boundary = 2
          boundaries.determination = 'good'
        }
      }

      // if the majority of availableGoodCriteria and the majority of availableOutstanding are selected
      if (gradedGood && gradedOutstanding) {
        const gradedOutstandingNegatable = this.getNegatableCount('outstanding')
        const gradedGoodNegatable = this.getNegatableCount('good')

        const majorityGood = Math.round(((availableGoodCriteria - gradedGoodNegatable) / 2) * 100) / 100
        const majorityOutstanding = Math.round(((availableOutstanding - gradedOutstandingNegatable) / 2) * 100) / 100

        if (gradedGood > majorityGood && gradedOutstanding > majorityOutstanding) {
          boundaries.boundary = 1
          boundaries.determination = 'outstanding'
        }
      }

      return boundaries
    },
    hasSelectedGrade() {
      return (
        Object.values(this.currentGrade.criteria).flatMap(val => val).length > 0
      )
    },
    savedSection() {
      const hasSection =
        this.currentAudit && Array.isArray(this.currentAudit.sections)
      return hasSection
        ? this.currentAudit.sections.find(s => s.id === this.id)
        : null
    },
    hasSelectedRi() {
      // MF Stop the app throwing an error when the section object doesn't exist.
      if (!this.currentAudit.sections) return false

      const section = this.currentAudit.sections.find(x => x.id === this.id)

      // MF Stop the app throwing an error when the section object doesn't exist.
      if (!section || !section.hasOwnProperty('criteria')) return

      return (
        section.criteria.requires_improvement &&
        section.criteria.requires_improvement.length > 0
      )
    }
  },
  watch: {
    'currentGrade.criteria': {
      handler() {
        this.currentGrade.grade = this.computedGrade
      },
      deep: true
    },
    '$route.params.id': {
      handler() {
        this.refreshSections()
        document.getElementById('accordion').scrollTop = 46
        if (this.savedSection) {
          this.currentGrade = { ...this.currentGrade, ...this.savedSection }
          this.openAccordion = this.savedSection.determination || 'good'
        } else {
          this.openAccordion = 'good'
        }
      }
    }
  },
  methods: {
    ...mapActions({ setGrade: 'audit/setGrade' }),
    ...mapMutations({ refreshSections: 'data/REFRESH_SECTIONS' }),
    async next() {
      try {
        await this.setGrade({
          id: this.id,
          name: this.currentSection.name,
          ...this.currentGrade,
          determination: this.gradeBoundaries.determination
        })
        this.resetPage()
        this.$emit('content-saved', { id: this.id })
        this.$router.push(`/plans/${this.nextPage}`)
      } catch (err) {
        console.log({ err })
      }
    },
    accordionToggled(event, selected) {
      // set div #accordion scrollTop to multiples of 46 depending on which is toggled
      if (
        event.target.innerText === 'Not Applicable' &&
        this.mustGradeSenHealth
      ) {
        displayWarning(message.MUST_GRADE_SEN_HEALTH, true)
      } else {
        this.openAccordion === selected
          ? (this.openAccordion = null)
          : (this.openAccordion = selected)
      }
    },
    updateNegatable(checked, id, criteria, selected) {
      if (id !== 'not-applicable' || !(criteria === 'good' || criteria === 'outstanding')) {
        return false
      }

      if (checked) {
        this.negatable[criteria].push(selected)
      } else {
        this.negatable[criteria] = this.negatable[criteria].filter((item) => item !== selected)
      }

      return true
    },
    accordionItemToggled(event, { criteria, selected }) {
      const { checked, id } = event.target
      let pushedItem = selected

      this.updateNegatable(checked, id, criteria, selected)

      if (id === 'not-applicable') {
        pushedItem = 'Not applicable - ' + selected
        if (this.currentGrade.criteria[criteria]) {
          // remove selected from this.currentGrade.criteria[criteria] where selected matches
          this.currentGrade.criteria[criteria] = this.currentGrade.criteria[
            criteria
          ].filter((item) => item !== selected)
        }
      }

      if (
        !Object.prototype.hasOwnProperty.call(
          this.currentGrade.criteria,
          criteria
        )
      ) {
        this.$set(this.currentGrade.criteria, criteria, [])
      }

      checked
        ? this.currentGrade.criteria[criteria].push(pushedItem)
        : (this.currentGrade.criteria[criteria] = this.currentGrade.criteria[
          criteria
        ].filter(existing => existing !== pushedItem))
      if (criteria === 'requires_improvement') {
        const hasSelectedMaster = this.currentGrade.criteria[criteria].includes(
          this.currentSection.criteria[criteria][0]
        )
        if (
          hasSelectedMaster &&
          this.currentGrade.criteria[criteria].length === 1
        ) {
          this.openAccordion = null
          setTimeout(() => {
            this.openAccordion = criteria
          }, 100)
        } else if (!hasSelectedMaster) {
          this.currentGrade.criteria[criteria] = []
        }
      }

      this.$emit('content-updated', { id: this.id })
    },
    resetPage() {
      this.currentGrade = {
        grade: null,
        comments: null,
        criteria: {}
      }
    },
    // MF - removed info for accordion
    getInfoForCriteria(criteria) {
      return '' // '(' + completed + '/' + total + ')'
    },
    getInitialBoundaryFromComptedGrade() {
      if (this.computedGrade > 0.5 && this.computedGrade <= 1.5) {
        return 3
      } else if (this.computedGrade > 1.5 && this.computedGrade <= 2.5) {
        return 2
      } else if (this.computedGrade > 2.5) {
        return 1
      }

      // if (this.computedGrade <= 0.5)
      return 4
    },
    getGradedCriteriaLength(criteria) {
      return this.gradedCriteria.hasOwnProperty(criteria)
        ? this.gradedCriteria[criteria]
          .filter((c) => !c.match(this.notApplicableRegex)).length
        : 0
    },
    // TODO: uses hardcode string to hack in new requirement. Needs to be refactored
    hasRequireImprovementCriteriaGraded(criteria) {
      if (
        criteria !== 'good' ||
        !this.currentSection.criteria.hasOwnProperty(criteria) ||
        !this.gradedCriteria.hasOwnProperty(criteria)
      ) {
        return true
      }

      const filterCurrentCriteria = (elem) => {
        return this.currentSection.criteria.good.includes(elem)
      }

      const currentMatched = requiresImprovementArray[criteria].filter(filterCurrentCriteria)

      if (currentMatched.length === 0) {
        return true
      }

      const filterCriteria = (elem) => {
        return this.gradedCriteria.good.includes(elem)
      }

      const matched = requiresImprovementArray[criteria].filter(filterCriteria)
      const matchedNegated = requiresImprovementArray[criteria].filter((elem) => {
        return this.negatable.good.includes(elem)
      })

      return (matched.length === (2 - matchedNegated.length))
    },
    isInNegatable(criteria, selected) {
      return this.negatable[criteria].includes(selected)
    },
    getNegatableCount(grade) {
      return this.negatable[grade].length
    },
    // TODO: MF this is a horrible and hacky way of giving some criteria half points. I will sleep badly because of this.
    // A refactor is needed so a score can be associated with each criteria
    getGoodExceptionHalfPoints(criteria) {
      if (
        criteria !== 'good' ||
        !this.currentSection.criteria.hasOwnProperty(criteria) ||
        !this.gradedCriteria.hasOwnProperty(criteria)
      ) {
        return 0
      }

      const filterCriteria = (elem) => {
        return this.gradedCriteria.good.includes(elem)
      }

      const matched = halfPointGoodCriteriaArray[criteria].filter(filterCriteria)

      return (matched.length > 1) ? matched.length * 0.5 : matched.length
    },
    // TODO: MF this is a horrible and hacky way of giving some criteria half points. I'll will sleep badly because of this.
    // A refactor is needed so a score can be associated with each criteria
    getGoodCurrentHalfPointLength(criteria) {
      if (
        criteria !== 'good' ||
        !this.currentSection.criteria.hasOwnProperty(criteria) ||
        !halfPointGoodCriteriaArray.hasOwnProperty(criteria)
      ) {
        return 0
      }

      const filterCriteria = (elem) => {
        return this.currentSection.criteria.good.includes(elem)
      }

      const matched = halfPointGoodCriteriaArray[criteria].filter(filterCriteria)

      return (matched.length > 1) ? matched.length * 0.5 : matched.length
    },
    getCurrentSectionLength(criteria) {
      return this.currentSection.criteria.hasOwnProperty(criteria)
        ? this.currentSection.criteria[criteria].length
        : 0
    },
    getNonApplicableCriteria(criteria) {
      return this.currentGrade.notApplicable &&
        this.currentGrade.notApplicable.hasOwnProperty(criteria)
        ? this.currentGrade.notApplicable[criteria]
        : 0
    },
    getNegatable(data) {
      const getNotApplicable = (property) => {
        if (!data[property]) return []

        return data[property].filter((c) => c.match(this.notApplicableRegex))
          .map((c) => c.replace(this.notApplicableRegex, ''))
      }
      this.negatable.outstanding = getNotApplicable('outstanding')
      this.negatable.good = getNotApplicable('good')
      this.negatable.requires_improvement = getNotApplicable('requires_improvement')
      this.negatable.inadequate = getNotApplicable('inadequate')
    }
  },
  mounted() {
    if (this.savedSection) {
      this.getNegatable(this.savedSection.criteria)
      this.currentGrade = this.savedSection
      this.openAccordion = this.hasSelectedRi
        ? 'requires_improvement'
        : this.savedSection.determination || 'good'
    } else {
      this.openAccordion = this.hasSelectedRi ? 'requires_improvement' : 'good'
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/style/variables.scss';
@import '@/style/components.scss';
@import '@/style/sections.scss';
@import '@/style/transitions.scss';
</style>
